import React, { useRef, useState, useEffect } from "react";
import { Controller, useController } from "react-hook-form";
import {
  Box,
  TextField,
  Divider,
  InputAdornment,
  Typography,
} from "@mui/material";
import AccessTime from "@mui/icons-material/AccessTime";
import i18next from "i18next";
import {
  DATE_LONG_TEXT,
  FIELD_REQUIRED,
  BUTTON_START_NOW,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { getReactHookFormNestedError } from "../../utils/errors";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import DateField from "./DateField";
import { BORDER_RADIUS } from "../../utils/constants";

const DateTimeField = React.forwardRef(function DateTimeField(
  {
    control,
    rules = {
      required: {
        value: true,
        message: i18next.t(FIELD_REQUIRED),
      },
    },
    label, // eslint-disable-line no-unused-vars
    minDate,
    maxDate,
    errors = {},
    name = "",
    disabled = false,
    actionsChange = () => {},
    onOpen = () => {},
    value = null,
    variant = "filled",
    mode = "input",
    sx = {},
    isDisabledClickText = false,
    onClickText = () => {},
    formatText = DATE_LONG_TEXT,
    propsText = {},
    className = "", // eslint-disable-line no-unused-vars
    fullWidth = true,
    clearable = false, // eslint-disable-line no-unused-vars
    id = "",
    renderDay = null,
    showStartNowButton = false,
    startNowSetsNull = false,
    publishNowButtonText = BUTTON_START_NOW, // eslint-disable-line no-unused-vars
    nowCustomPlaceholder = null, // eslint-disable-line no-unused-vars
  },
  ref
) {
  const refIcon = useRef(null);

  const error = getReactHookFormNestedError(errors, name);
  const { t } = useTranslationApp();

  const sxMode =
    mode === "text"
      ? {
          visibility: "hidden",
          position: "absolute",
        }
      : {};

  const {
    field: { value: fieldValue, onChange: onChangeField },
  } = useController({
    control,
    name,
    rules,
  });

  const getDateFromValue = (val) => {
    if (!val) return null;
    if (val instanceof Date) return val;
    if (typeof val === "number") return new Date(val);
    return null;
  };

  const [date, setDate] = useState(() => {
    const initialValue = fieldValue || value;
    return getDateFromValue(initialValue);
  });

  useEffect(() => {
    const newDate = getDateFromValue(fieldValue);
    const currentTime = date?.getTime();
    const newTime = newDate?.getTime();
    if (currentTime !== newTime) {
      setDate(newDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValue]);

  const onStartNow = () => {
    if (startNowSetsNull) {
      onChangeField(null);
      setDate(null);
      actionsChange(null);
    } else {
      const now = new Date();
      updateValue(now);
    }
  };

  const updateValue = (newDate) => {
    const isValid = newDate && !isNaN(newDate.getTime());
    const finalValue = isValid ? newDate : null;

    onChangeField(finalValue);
    setDate(finalValue);
    actionsChange(finalValue);
  };

  const onTimeChange = (event) => {
    const timeString = event.target.value;
    if (!timeString) return;

    const [hours, minutes] = timeString.split(":").map(Number);
    const current = date ? new Date(date) : new Date();

    current.setHours(hours);
    current.setMinutes(minutes);
    current.setSeconds(0);
    current.setMilliseconds(0);

    updateValue(current);
  };

  const timeValue =
    date != null
      ? `${String(new Date(date).getHours()).padStart(2, "0")}:${String(
          new Date(date).getMinutes()
        ).padStart(2, "0")}`
      : "";

  const onDateChange = (newDate) => {
    if (!newDate || isNaN(newDate.getTime())) {
      updateValue(null);
      return;
    }

    const prevTime = date ? new Date(date) : new Date(0, 0, 0, 0, 0);
    const updated = new Date(newDate);

    const isMidnight =
      updated.getHours() === 0 &&
      updated.getMinutes() === 0 &&
      updated.getSeconds() === 0;

    if (isMidnight) {
      updated.setHours(prevTime.getHours());
      updated.setMinutes(prevTime.getMinutes());
      updated.setSeconds(0);
      updated.setMilliseconds(0);
    }

    updateValue(updated);
  };

  const commonSX = {
    flex: 1,
    "& fieldset": { border: "none" },
    "& input": { textAlign: "center" },
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: fullWidth ? "100%" : "auto",
      }}
    >
      {mode === "text" && (
        <Typography
          variant="h6"
          {...propsText}
          sx={{
            cursor: "pointer",
            textDecoration: "underline",
            "&:hover": {
              cursor: "pointer",
              textDecoration: "underline",
            },
            ...(propsText?.sx || {}),
          }}
          onClick={() => {
            if (isDisabledClickText) return;
            const child = refIcon.current?.children?.[1];
            if (child) {
              child.click();
              onClickText();
            }
          }}
        >
          {t(formatText, {
            date,
          })}
        </Typography>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <Controller
          name={name}
          defaultValue={value}
          control={control}
          rules={rules}
          render={() => (
            <Box
              ref={ref}
              sx={(theme) => ({
                display: "flex",
                flexDirection: "row",
                borderRadius: BORDER_RADIUS,
                overflow: "hidden",
                width: "100%",
                border: `1px solid ${error ? theme.palette.error.main : INPUT_COLOR_BORDER}`,
                boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                backgroundColor: "fields.background",
                "@media (max-width: 400px)": {
                  flexDirection: "column",
                },
                ...sx,
                ...sxMode,
              })}
            >
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0,
                }}
              >
                <DateField
                  name={name}
                  control={control}
                  label={null}
                  value={date}
                  minDate={minDate}
                  maxDate={maxDate}
                  actionsChange={onDateChange}
                  fullWidth
                  variant={variant}
                  sx={{
                    ...commonSX,
                    "& .MuiInputBase-root": {
                      paddingRight: 0,
                    },
                    "& fieldset": { border: "none" },
                  }}
                  disabled={disabled}
                  onOpen={onOpen}
                  renderDay={renderDay}
                  errors={{}}
                  showStartNowButton={showStartNowButton}
                  onClickStartNow={onStartNow}
                />
              </Box>

              <Divider
                orientation="horizontal"
                flexItem
                sx={{
                  display: "none",
                  bgcolor: INPUT_COLOR_BORDER,
                  "@media (max-width: 400px)": {
                    display: "block",
                  },
                }}
              />

              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  bgcolor: INPUT_COLOR_BORDER,
                  alignSelf: "stretch",
                  "@media (max-width: 400px)": {
                    display: "none",
                  },
                }}
              />

              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0,
                }}
              >
                <TextField
                  id={id}
                  name={`${name}_time`}
                  type="time"
                  value={timeValue}
                  onChange={onTimeChange}
                  disabled={disabled || !date}
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" ref={refIcon}>
                        <AccessTime fontSize="medium" />
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{
                    step: 300,
                  }}
                  sx={{
                    ...commonSX,
                    "& .MuiInputBase-root": {
                      py: 2,
                      pl: 1,
                    },
                    "& input": {
                      pr: 0,
                      textAlign: "center",
                      cursor: "pointer",
                    },
                    "& input[type='time']::-webkit-calendar-picker-indicator": {
                      display: "none",
                    },
                    "& input[type='time']::-webkit-inner-spin-button": {
                      display: "none",
                    },
                    "& .MuiInputBase-root.Mui-disabled .MuiInputBase-input": {
                      WebkitTextFillColor: "rgba(0, 0, 0, 0.5) !important",
                    },
                  }}
                />
              </Box>
            </Box>
          )}
        />

        {error?.message && (
          <Typography variant="caption" color="error">
            {error?.message}
          </Typography>
        )}
      </Box>
    </Box>
  );
});

export default DateTimeField;
